// src/components/layout/Sidebar.tsx
import React from 'react';
import { Layout, Menu, Typography, Divider, Drawer } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  WalletOutlined,
  LogoutOutlined,
  UserOutlined,
  TagOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useAuthContext } from '../contexts/AuthContext';
import { useLogout } from '../../features/auth/hooks/useLogout';

const { Sider } = Layout;
const { Title, Text } = Typography;

interface SidebarProps {
  collapsed?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, isMobile = false, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { logout } = useLogout();
  const version = process.env.REACT_APP_VERSION || 'unknown';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
    if (onClose) onClose();
  };

  // Base path for all application routes after login
  const basePath = '/app';

  const getSelectedKey = () => {
    const currentPath = location.pathname;
    if (currentPath.startsWith(`${basePath}/pay-onay`)) return `${basePath}/pay-onay`;
    if (currentPath.startsWith(`${basePath}/payments`)) return `${basePath}/payments`;
    return `${basePath}/pay-onay`; // Default to pay-onay
  };

  const menuItems = [
    {
      key: `${basePath}/pay-onay`,
      icon: <WalletOutlined />,
      label: <Link to={`${basePath}/pay-onay`} onClick={onClose}>Пополнение Onay</Link>,
    },
    {
      key: `${basePath}/payments`,
      icon: <UnorderedListOutlined />,
      label: <Link to={`${basePath}/payments`} onClick={onClose}>История платежей</Link>,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выход',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const sidebarContent = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px', textAlign: 'center', color: 'white' }}>
        <Title level={4} style={{ color: 'white', margin: 0 }}>Promoter App</Title>
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        items={menuItems}
        style={{ flex: 1, borderRight: 0 }}
      />
      <Divider style={{ margin: '8px 0', borderColor: 'rgba(255,255,255,0.1)' }} />
      <div style={{ padding: '12px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.65)' }}>
        <div style={{ marginBottom: '8px' }}>
          <UserOutlined style={{ marginRight: '4px' }} />
          <Text strong style={{ color: 'rgba(255,255,255,0.85)' }}>{user}</Text>
        </div>
        <Divider style={{ margin: '8px 0', borderColor: 'rgba(255,255,255,0.1)' }} />
        <div style={{ marginTop: '4px' }}>
          <TagOutlined style={{ marginRight: '4px' }} />
          <Text style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)' }}>
            Версия: {version}
          </Text>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        onClose={onClose}
        open={!collapsed}
        closable={false}
        width={220}
        styles={{
          body: { padding: 0, background: '#001529' }
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Sider width={220} theme="dark">
      {sidebarContent}
    </Sider>
  );
};

export default Sidebar;
